import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import { loadCustomers, saveCustomer } from "../../data/customers";
import { getCustomerGroups } from "../../data/customerGroups";

const emptyForm = {
  title: "",
  type: "client",
  firstName: "",
  lastName: "",
  company: "",
  phone: "",
  email: "",
  address: "",
  postcode: "",
  city: "",
  billingAddress: "",
  billingPostcode: "",
  billingCity: "",
  notes: "",
  active: true,
  newsletter: false,
  newsletterOptin: false,
  totalOrders: 0,
  totalSpent: 0,
  lastVisit: null,
  groupId: "",
};

const MyAccountCustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loaderStatus, setLoaderStatus] = useState(Boolean(id));
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);

  useEffect(() => {
    setCustomerGroups(getCustomerGroups());
    
    if (!id) return;
    let isMounted = true;
    loadCustomers().then((loaded) => {
      if (!isMounted) return;
      const customer = loaded.find((c) => c.id === id);
      if (!customer) {
        setError("Client introuvable.");
        setLoaderStatus(false);
        return;
      }
      setEditingId(customer.id);
      setForm({
        title: customer.title || "",
        type: customer.type || "client",
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        company: customer.company || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        postcode: customer.postcode || "",
        city: customer.city || "",
        billingAddress: customer.billingAddress || "",
        billingPostcode: customer.billingPostcode || "",
        billingCity: customer.billingCity || "",
        notes: customer.notes || "",
        active: customer.active !== false,
        newsletter: Boolean(customer.newsletter),
        newsletterOptin: Boolean(customer.newsletterOptin),
        totalOrders: Number(customer.totalOrders || 0),
        totalSpent: Number(customer.totalSpent || 0),
        lastVisit: customer.lastVisit || null,
        groupId: customer.groupId || "",
      });
      setLoaderStatus(false);
    });
    return () => { isMounted = false; };
  }, [id]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) {
      setError("Le nom est obligatoire.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await saveCustomer({
        id: editingId || undefined,
        ...form,
        lastVisit: editingId ? (form.lastVisit || null) : new Date().toISOString(),
      });
      navigate("/MyAccountCustomers");
    } catch {
      setError("Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loaderStatus) {
    return (
      <div>
        <ScrollToTop />
        <section className="admin-section">
          <div className="container">
            <div className="row">
              <AdminSidebar activePath="/MyAccountCustomers" />
              <div className="col-lg-9 col-md-8 col-12">
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">Chargement du client...</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountCustomers" />
            <div className="col-lg-9 col-md-8 col-12">
              <div className="p-6 p-lg-10">
                <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                  <div>
                    <h2 className="mb-1">{editingId ? "Modifier le client" : "Ajouter un client"}</h2>
                    <p className="text-muted mb-0">
                      {editingId ? "Modifiez les informations du client puis enregistrez." : "Renseignez les informations du nouveau client puis enregistrez."}
                    </p>
                  </div>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/MyAccountCustomers")}>
                    Retour a la liste
                  </button>
                </div>

                <div className="row justify-content-center g-4">
                  <div className="col-lg-8 col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-4">
                        {error && <div className="alert alert-danger py-2">{error}</div>}

                        <h6 className="text-uppercase small fw-semibold text-muted mb-3">Identite</h6>
                        <div className="row g-3">
                          <div className="col-4 col-sm-3">
                            <label className="form-label small">Titre</label>
                            <select className="form-select" value={form.title} onChange={(e) => update({ title: e.target.value })}>
                              <option value="">—</option>
                              <option value="M.">M.</option>
                              <option value="Mme">Mme</option>
                            </select>
                          </div>
                          <div className="col-4 col-sm-3">
                            <label className="form-label small">Prenom</label>
                            <input type="text" className="form-control" value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} placeholder="Prenom" />
                          </div>
                          <div className="col-4 col-sm-3">
                            <label className="form-label small">Nom</label>
                            <input type="text" className="form-control" value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} placeholder="Nom" />
                          </div>
                          <div className="col-12 col-sm-3">
                            <label className="form-label small">Societe</label>
                            <input type="text" className="form-control" value={form.company} onChange={(e) => update({ company: e.target.value })} placeholder="Societe" />
                          </div>
                        </div>

                        <div className="row g-3 mt-1">
                          <div className="col-12 col-sm-3">
                            <label className="form-label small">Categorie</label>
                            <select className="form-select" value={form.type} onChange={(e) => update({ type: e.target.value })}>
                              <option value="client">Client</option>
                              <option value="visiteur">Visiteur</option>
                              <option value="boutique">Boutique</option>
                              <option value="restaurant">Restaurant</option>
                              <option value="pro">Pro</option>
                            </select>
                          </div>
                          <div className="col-12 col-sm-4">
                            <label className="form-label small">Groupe de prix (Variant)</label>
                            <select className="form-select" value={form.groupId} onChange={(e) => update({ groupId: e.target.value })}>
                              <option value="">Aucun groupe (Prix par défaut)</option>
                              {customerGroups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <h6 className="text-uppercase small fw-semibold text-muted mb-3 mt-4">Contact</h6>
                        <div className="row g-3">
                          <div className="col-md-5">
                            <label className="form-label small">Telephone</label>
                            <input type="tel" className="form-control" value={form.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="06 12 34 56 78" />
                          </div>
                          <div className="col-md-7">
                            <label className="form-label small">Email</label>
                            <input type="email" className="form-control" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="email@exemple.com" />
                          </div>
                          <div className="col-12">
                            <div className="d-flex flex-wrap gap-4 pt-1">
                              <div className="form-check form-switch mb-0">
                                <input className="form-check-input" type="checkbox" role="switch" id="ck-active" checked={form.active} onChange={(e) => update({ active: e.target.checked })} />
                                <label className="form-check-label" htmlFor="ck-active">Actif</label>
                              </div>
                              <div className="form-check form-switch mb-0">
                                <input className="form-check-input" type="checkbox" role="switch" id="ck-newsletter" checked={form.newsletter} onChange={(e) => update({ newsletter: e.target.checked })} />
                                <label className="form-check-label" htmlFor="ck-newsletter">Newsletter</label>
                              </div>
                              <div className="form-check form-switch mb-0">
                                <input className="form-check-input" type="checkbox" role="switch" id="ck-optin" checked={form.newsletterOptin} onChange={(e) => update({ newsletterOptin: e.target.checked })} />
                                <label className="form-check-label" htmlFor="ck-optin">Opt-in commercial</label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <h6 className="text-uppercase small fw-semibold text-muted mb-3 mt-4">Adresse de livraison</h6>
                        <div className="row g-3">
                          <div className="col-12">
                            <input type="text" className="form-control" value={form.address} onChange={(e) => update({ address: e.target.value })} placeholder="15 Rue des Lilas" />
                          </div>
                          <div className="col-5">
                            <label className="form-label small">Code postal</label>
                            <input type="text" className="form-control" value={form.postcode} onChange={(e) => update({ postcode: e.target.value })} placeholder="91000" />
                          </div>
                          <div className="col-7">
                            <label className="form-label small">Ville</label>
                            <input type="text" className="form-control" value={form.city} onChange={(e) => update({ city: e.target.value })} placeholder="Evry" />
                          </div>
                        </div>

                        <h6 className="text-uppercase small fw-semibold text-muted mb-3 mt-4">Adresse de facturation</h6>
                        <div className="row g-3">
                          <div className="col-12">
                            <input type="text" className="form-control" value={form.billingAddress} onChange={(e) => update({ billingAddress: e.target.value })} placeholder="15 Rue des Lilas" />
                          </div>
                          <div className="col-5">
                            <label className="form-label small">Code postal</label>
                            <input type="text" className="form-control" value={form.billingPostcode} onChange={(e) => update({ billingPostcode: e.target.value })} placeholder="91000" />
                          </div>
                          <div className="col-7">
                            <label className="form-label small">Ville</label>
                            <input type="text" className="form-control" value={form.billingCity} onChange={(e) => update({ billingCity: e.target.value })} placeholder="Evry" />
                          </div>
                        </div>

                        <h6 className="text-uppercase small fw-semibold text-muted mb-3 mt-4">Notes internes</h6>
                        <textarea className="form-control" rows="3" value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Notes sur le client..." />

                        <div className="d-flex gap-2 mt-4">
                          <button type="button" className="btn btn-dark flex-grow-1" onClick={handleSave} disabled={saving}>
                            {saving ? "Enregistrement..." : (editingId ? "Enregistrer les modifications" : "Enregistrer le client")}
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/MyAccountCustomers")}>Annuler</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountCustomerForm;